import type { AuthUser, Permissions } from '@store/authStore';

export type PermissionPage =
  | 'dashboard'
  | 'phishing_sim'
  | 'templates'
  | 'training'
  | 'content_library'
  | 'users'
  | 'groups'
  | 'roles'
  | 'reports'
  | 'settings'
  | 'xai';

export interface ProtectedRouteConfig {
  path: string;
  page: PermissionPage;
}

export const pageFallbackPath: Record<PermissionPage, string> = {
  dashboard: '/dashboard',
  phishing_sim: '/campaigns',
  templates: '/campaigns/templates',
  training: '/training',
  content_library: '/content',
  users: '/users',
  groups: '/groups',
  roles: '/roles',
  reports: '/reports',
  settings: '/settings',
  xai: '/xai',
};

export function getUserPermissions(user: AuthUser | null): Permissions {
  if (!user) return {};
  return user.permissions ?? {};
}

export function isUserAdmin(user: AuthUser | null): boolean {
  if (!user) return false;
  return (
    user.roles?.includes('super_admin') === true ||
    user.roles?.some((r) => r.toLowerCase().includes('admin')) === true
  );
}

const END_USER_ALLOWED_PAGES: PermissionPage[] = ['dashboard', 'training'];

export function canViewPage(user: AuthUser | null, page: PermissionPage): boolean {
  if (!user) return false;
  if (user.roles?.includes('super_admin')) return true;

  const isAdmin = isUserAdmin(user);
  const permissions = getUserPermissions(user);

  if (permissions && Array.isArray(permissions[page]) && permissions[page].length > 0) {
    return permissions[page].includes('view');
  }

  // Non-admin standard users are strictly limited to end-user pages
  if (!isAdmin) {
    return END_USER_ALLOWED_PAGES.includes(page);
  }

  return true;
}

export function getFirstAllowedPath(user: AuthUser | null) {
  if (!user) return '/login';
  return pageFallbackPath.dashboard;
}
